/**
 * permissions.js — Slice 1 role-aware UI gating.
 *
 * IMPORTANT: this is UI/UX only. It hides or disables controls to keep the
 * interface clean per role; it is NOT a security boundary. The real boundary
 * is the de-hub API (added in a later slice). Never rely on this to protect
 * data — a determined user can flip a localStorage key or edit the bundle.
 *
 * Roles originate from the Azure AD token's `roles` claim (App Roles). The
 * broker-deals roles are the `BD.*` strings below. The platform roles
 * `de` / `admin` are superusers and bypass every check.
 */

/** Platform superuser roles — bypass every permission check. */
export const SUPERUSER = ["de", "admin"];

/** Full broker-deals admin App Role. Bypasses every check like SUPERUSER, but
 * scoped to this app (it only ever appears in broker-deals tokens). */
export const APP_ADMIN = "BD.Admin";

/** Every role that bypasses all checks: platform superusers + the app admin. */
const FULL_BYPASS = [...SUPERUSER, APP_ADMIN];

/** The broker-deals App Roles. Anything outside this set (other than
 * SUPERUSER) grants no gated access on its own — only explicit membership in
 * an action's allowed list (or a `"*"` action) does. */
export const BD_ROLES = [
  "BD.BrokerAgent",
  "BD.Landman",
  "BD.LandTech",
  "BD.TitleAgent",
  "BD.LeaseRecords",
  "BD.DivisionOrders",
  "BD.Accounting",
  "BD.CapexApprover",
  "BD.Admin",
  "BD.Land",
];

/**
 * The INTERNAL job roles — everything except the external `BD.BrokerAgent`
 * and the `BD.Admin` app admin. `BD.Land` is the union of exactly these.
 */
export const INTERNAL_ROLES = [
  "BD.Landman",
  "BD.LandTech",
  "BD.TitleAgent",
  "BD.LeaseRecords",
  "BD.DivisionOrders",
  "BD.Accounting",
  "BD.CapexApprover",
];

/**
 * Composite role -> the roles it stands in for. Mirrors `ROLE_EXPANSIONS` in
 * de-hub's `permissions.py`, which is the enforcing side.
 *
 * `BD.Land` exists because an Entra app-role assignment holds exactly ONE
 * role while the land team's people wear several hats — one combined role
 * beats five stacked assignments per user. It is NOT an admin: it is absent
 * from `FULL_BYPASS`, so every `admin.*` action (all of which allow only
 * `["BD.Admin"]`) still denies it. That is why managing the broker-agent
 * roster is `brokers.manageAgents` and not `admin.brokerAccess` — it is land
 * team work, so it lives outside the admin family.
 *
 * Expansion happens inside `can`, so no entry in PERMISSIONS below ever names
 * `BD.Land` and none of them can drift from it — a future action that lists
 * any internal role covers `BD.Land` automatically.
 */
export const ROLE_EXPANSIONS = {
  "BD.Land": INTERNAL_ROLES,
};

/**
 * Action -> allowed roles. A list containing the sentinel `"*"` means "any
 * authenticated role". An empty list means "superuser only" (reachable only
 * via the SUPERUSER bypass).
 */
export const PERMISSIONS = {
  "deal.create": ["BD.BrokerAgent", "BD.Landman"],
  "deal.editTerms": ["BD.BrokerAgent", "BD.Landman"],
  // Who sees Bulk Edit on the deals list: the same roles POST
  // /deals/bulk_update admits. Each still sees only the fields it may edit
  // on one deal (canEditField), so this grants no new edit right.
  // Feedback #42 (kmp): Land Tech, Lease Records and Division Orders too.
  "deal.bulkEdit": [
    "BD.BrokerAgent",
    "BD.Landman",
    "BD.LandTech",
    "BD.LeaseRecords",
    "BD.DivisionOrders",
  ],
  "deal.editStatus": ["BD.BrokerAgent", "BD.Landman", "BD.LandTech"],
  // Reassigning a deal to a different BROKERAGE. WIG's call alone: for a
  // tenancy-scoped BD.BrokerAgent it would mean handing their own deal to
  // another brokerage. Mirrors `brokerage_id: [LANDMAN]` on the server.
  "deal.assign": ["BD.Landman"],
  // The two assignment PEOPLE fields — `agent` (the brokerage's own staff on
  // the deal) and `warwick_landman_id` (the WIG landman they deal with). The
  // whole land team plus the brokerage: both are columns of the broker upload
  // template, so a brokerage already supplies them on intake and must be able
  // to correct them afterwards, and naming who is working a deal is
  // record-keeping a land tech does day to day — distinct from `deal.assign`
  // above, which moves the deal to another brokerage. Mirrors
  // `[BROKER_AGENT] + LAND_TEAM_ROLES` on the server.
  "deal.assignPeople": ["BD.BrokerAgent", "BD.Landman", "BD.LandTech"],
  // Deleting a deal takes its whole subtree with it (lease/deeds + tracts,
  // wells + well tracts, entities, approvals, status history, contact log,
  // payments, remarks, attachments). Open to the working roles that maintain
  // deals day to day; BD.Admin and de/admin also reach it via FULL_BYPASS.
  "deal.delete": ["BD.BrokerAgent", "BD.Landman", "BD.LandTech"],
  "record.edit": ["BD.BrokerAgent", "BD.Landman", "BD.LandTech"],
  // Attaching a buyer/seller to a deal (POST /deal_entities) is deliberately
  // WIDER than record.edit: the post-close roles add parties as part of their
  // workflow (UAT #9). Editing or removing one stays on record.edit, matching
  // PUT/DELETE /deal_entities.
  "dealEntity.create": [
    "BD.BrokerAgent",
    "BD.Landman",
    "BD.LandTech",
    "BD.LeaseRecords",
    "BD.DivisionOrders",
  ],
  "contactLog.edit": ["BD.BrokerAgent", "BD.Landman"],
  "remarks.add": ["*"], // any role
  "attachments.upload": [
    "BD.BrokerAgent",
    "BD.Landman",
    "BD.LandTech",
    "BD.TitleAgent",
    "BD.Accounting",
  ],
  // Filing a document under the TITLE OPINION type. Narrower than
  // `attachments.upload`: a title opinion is a work product of the internal
  // land team / title examiner, so an external brokerage must not be able to
  // file one — every other uploader role keeps it. See
  // ATTACHMENT_TYPE_ACTIONS below for how the dropdown consumes this.
  "attachments.uploadTitleOpinion": [
    "BD.Landman",
    "BD.LandTech",
    "BD.TitleAgent",
    "BD.Accounting",
  ],
  "offer.submit": ["BD.BrokerAgent", "BD.Landman"],
  "offer.approve": ["BD.CapexApprover"],
  "pay.submit": ["BD.BrokerAgent", "BD.Landman"],
  "pay.approve": ["BD.CapexApprover"],
  "approvalQueue.view": ["BD.CapexApprover"],
  "title.dates": ["BD.Landman", "BD.LandTech"],
  "title.opinion": ["BD.LandTech", "BD.TitleAgent"],
  curative: ["BD.BrokerAgent", "BD.Landman", "BD.LandTech"],
  // Submitting a payment REQUEST — asking for a check to be cut. Mirrors
  // de-hub's PAYMENT_CREATE_ROLES on POST /payments. BD.Landman is confined to
  // the request columns (payment_type, payment_amount, check_requested_date,
  // paid_by_entity_id) by CHILD_FIELD_LIMITS["payments"], and is add-only —
  // absent from PUT/DELETE /payments. BD.BrokerAgent holds this action too,
  // but is no longer confined by it; see `payment.recordCheck`.
  "payment.request": ["BD.BrokerAgent", "BD.Landman"],
  // Recording what actually happened to the check — number, date paid, method
  // of delivery, tracking. Authorizes EDITING a payment row and writing the
  // fulfillment columns on the Add form.
  //
  // BD.BrokerAgent was added per UAT ticket #61: the brokerage is the party
  // that cuts and sends the check, so it owns the check number, delivery
  // method and tracking number, and was being made to ask Accounting to type
  // them in. Mirrors de-hub's PAYMENT_UPDATE_ROLES and the dropped
  // CHILD_FIELD_LIMITS["payments"] entry for that role.
  //
  // BD.LandTech stays out per UAT: a land tech may not enter a payment, and is
  // view-only on the Payments tab — they still SEE every payment row (GET
  // /payments is ALL_BD) and keep every other write they had (deal status,
  // recordings, title dates, curative, attachments).
  "payment.recordCheck": ["BD.Accounting", "BD.BrokerAgent"],
  // Deleting a payment row — NOT implied by editing one. A payment is itself a
  // polymorphic parent: removing it takes its remarks and attachments (and
  // their blobs) with it, which is not a correction a requester should be able
  // to make alone. Mirrors de-hub, where DELETE /payments stayed [ACCOUNTING]
  // when #61 opened PUT.
  "payment.delete": ["BD.Accounting"],
  "recording.record": ["BD.Landman", "BD.LandTech"],
  // Deal-level handoff dates (`title_reports_integrated`,
  // `material_contracts_integrated`) — the post-close teams record when the
  // deal's paperwork was integrated into the downstream land system. BOTH
  // roles own BOTH dates; this is not split lease-vs-well. Mirrors the
  // server's DEAL_FIELD_ROLE_GROUPS entry exactly.
  //
  // (Replaces the never-referenced `integration.lease` / `integration.well`
  // keys, which split the roles in a way the server never did and were wired
  // to nothing.)
  "deal.editIntegration": ["BD.LeaseRecords", "BD.DivisionOrders"],
  // Operator-notification fields (`operator_notified`,
  // `operator_notified_date`). Mirrors DEAL_FIELD_ROLE_GROUPS.
  "deal.editOperatorNotice": ["BD.Landman", "BD.LandTech"],
  // Record-keeping fields — `aoi_id` (which AOI the deal sits in) and
  // `op_non_op` (is the acreage operated). The land team maintains them day to
  // day, and so does the submitting brokerage: the upload template asks for
  // both on intake, so a broker has to be able to fix them on the deal too.
  // The post-close / approval roles stay out — their scripts test deal editing
  // as blocked. Mirrors `[BROKER_AGENT] + LAND_TEAM_ROLES` on the server.
  "deal.editRecordKeeping": ["BD.BrokerAgent", "BD.Landman", "BD.LandTech"],
  // Promote economics (`promote`, `promote_due_date`) — a commercial term, so
  // Landman only, matching the WEG half of the terms group.
  "deal.editPromote": ["BD.Landman"],
  "deal.viewAll": [
    "BD.Landman",
    "BD.LandTech",
    "BD.TitleAgent",
    "BD.LeaseRecords",
    "BD.DivisionOrders",
    "BD.Accounting",
    "BD.CapexApprover",
  ],
  // The Brokers screen (broker master + the Agents child panel) is an internal
  // roster: every brokerage we work with, their agents, and their deal counts.
  // The outside parties must not see it — a BD.BrokerAgent would be reading
  // the competing brokerages, and a BD.TitleAgent has no reason to see the
  // vendor roster at all. Writing is separately gated: the agent roster to
  // `brokers.manageAgents`, the brokerage master to `admin.brokers`.
  "brokers.view": [
    "BD.Landman",
    "BD.LandTech",
    "BD.LeaseRecords",
    "BD.DivisionOrders",
    "BD.Accounting",
    "BD.CapexApprover",
  ],
  // Manage the per-brokerage agent roster (the email → brokerage mappings that
  // scope an external BD.BrokerAgent). Deliberately NOT an `admin.*` action:
  // agent turnover at a brokerage is the land team's own relationship
  // management, so the landman who knows the roster maintains it rather than
  // filing a ticket with an app admin. BD.Land inherits it via expansion.
  //
  // BD.BrokerAgent must never appear here — writing this table is writing your
  // own tenancy scope. Mirrors the server, where POST/PUT/DELETE
  // /broker_agent_access are gated to BD.Landman.
  "brokers.manageAgents": ["BD.Landman"],
  // Admin — manage dropdown vocabularies (pick lists). Gated to the new
  // BD.Admin role; de/admin superusers bypass via rule (a).
  "admin.pickLists": ["BD.Admin"],
  // Saved searches — publish ("Save for all users") and change the shared
  // user_id="global" searches on the Deals list. Gated to BD.Admin; de/admin
  // superusers bypass via rule (a). Mirrors de-hub's app-share check, which is
  // the boundary: global writes from anyone else 403.
  "savedSearches.saveForAll": ["BD.Admin"],
  // Admin — manage the shared entity master (Buyer/Seller vocabulary).
  // Gated to BD.Admin; de/admin superusers bypass via rule (a).
  "admin.entities": ["BD.Admin"],
  // Admin — manage the shared operator master (Well operator vocabulary).
  // Gated to BD.Admin; de/admin superusers bypass via rule (a).
  "admin.operators": ["BD.Admin"],
  // Admin — manage the AOI master (Area of Interest vocabulary). Gated to
  // BD.Admin; de/admin superusers bypass via rule (a). Matches the server:
  // BOTH POST and PUT /aois are admin-only, because AOIs are curated prospect
  // inventory rather than free-text parties (contrast POST /operators, which
  // deal editors may call for create-on-the-fly).
  "admin.aois": ["BD.Admin"],
  // Admin — manage the WIG land-team roster (the `warwick_landman` FK table
  // behind the deal form's Warwick Landman picker). Gated to BD.Admin;
  // de/admin superusers bypass via rule (a). Note this is about EDITING the
  // roster, not about being on it: `BD.Landman` is the job role a landman
  // holds, and deliberately does not grant maintenance of the list — same
  // split as every other `admin.*` key.
  "admin.landmen": ["BD.Admin"],
  // Admin — manage the broker master (add/edit/deactivate brokers).
  // BD.Admin + de/admin superusers only.
  "admin.brokers": ["BD.Admin"],
  // In-app reports (#239). READING is every BD role — the server scopes each
  // widget's rows exactly as the dataset's list endpoint does, so a broker
  // agent's report only ever counts their own brokerage's deals. Listed as
  // BD_ROLES rather than "*" so a user with no BD role (which the server
  // refuses anyway, ALL_BD) gets no Reports tab either.
  "reports.view": [...BD_ROLES],
  // Authoring (create / edit / publish / unpublish / delete). Mirrors
  // `@requires_roles([ADMIN])` on POST/PUT/DELETE /reports.
  "reports.manage": ["BD.Admin"],
  // A report's audience (internal / external / all) is land-team context:
  // shown as a tag to internal users only. The server decides who may open
  // which report; this only decides who is told the audience.
  "reports.seeAudience": [...INTERNAL_ROLES],
  // NOTE: `admin.feedback` and `admin.announcements` used to live here. The
  // feedback queue and the announcements now come from de-hub's shared
  // `app_support` module, which decides admin-ness per app from its own
  // APP_SUPPORT_ADMIN_ROLES config. Keeping a second, local answer to the
  // same question is how the two drift apart, so the client asks one source
  // instead: `hasAdminRole()` / `useIsAdmin()`, driven by VITE_ADMIN_ROLE
  // (see src/config/appSupport.js). Do not re-add them here.
};

/**
 * Deal field -> the action that governs writing it. Mirrors the server's
 * `DEAL_FIELD_ROLE_GROUPS` (de_api/broker_deals/permissions.py), which is the
 * enforcing boundary: a PUT changing a field the caller isn't in the group for
 * 403s naming the field.
 *
 * This map is a FALLBACK. `GET /metadata?table_name=deals` returns
 * `editable_for_me` per row, computed from the server's own map — prefer that
 * (see `canEditField`) so the two can't drift. The fallback keeps the UI
 * correct against a de-hub that predates that field.
 *
 * Fields absent here fall to `DEAL_FIELD_DEFAULT_ACTION`, matching the
 * server's `DEAL_FIELD_DEFAULT_ROLES` — new deal columns are terms until
 * someone says otherwise, on both sides.
 */
export const DEAL_FIELD_ACTIONS = {
  brokerage_id: "deal.assign",
  agent: "deal.assignPeople",
  warwick_landman_id: "deal.assignPeople",
  deal_status: "deal.editStatus",
  title_reports_integrated: "deal.editIntegration",
  material_contracts_integrated: "deal.editIntegration",
  operator_notified: "deal.editOperatorNotice",
  operator_notified_date: "deal.editOperatorNotice",
  aoi_id: "deal.editRecordKeeping",
  op_non_op: "deal.editRecordKeeping",
  promote: "deal.editPromote",
  promote_due_date: "deal.editPromote",
};

/** Governs every deal field not named in `DEAL_FIELD_ACTIONS`. */
export const DEAL_FIELD_DEFAULT_ACTION = "deal.editTerms";

/**
 * Attachment document types that are NOT open to every role holding
 * `attachments.upload`. Keyed by the pick-list `value` UPPER-CASED (the values
 * are seeded upper-case by de-hub migration 058/079, but an admin editing the
 * vocabulary through Admin → Pick Lists could re-case one), pointing at the
 * action that governs choosing it.
 *
 * Types absent here are open to every uploader.
 */
export const ATTACHMENT_TYPE_ACTIONS = {
  "TITLE OPINION": "attachments.uploadTitleOpinion",
};

/** Normalize an unknown roles input into a string array. */
const asArray = (roles) => (Array.isArray(roles) ? roles : []);

/** Do two role lists intersect? */
const intersects = (a, b) => a.some((r) => b.includes(r));

/**
 * A role list with every composite role (see `ROLE_EXPANSIONS`) replaced by
 * the roles it stands in for. The composite itself is KEPT, so `hasRole`
 * still answers truthfully about what Entra actually assigned.
 *
 * @param {string[]} roles
 * @returns {string[]}
 */
export const expandRoles = (roles) => {
  const list = asArray(roles);
  const expanded = new Set(list);
  for (const [composite, members] of Object.entries(ROLE_EXPANSIONS)) {
    if (expanded.has(composite)) members.forEach((r) => expanded.add(r));
  }
  return [...expanded];
};

/**
 * Does this role set contain a specific role string?
 * @param {string[]} roles - the caller's roles
 * @param {string} role - the role to test for
 * @returns {boolean}
 */
export const hasRole = (roles, role) => asArray(roles).includes(role);

/**
 * Does this role set have full-access bypass — a platform superuser
 * (`de` / `admin`) or the `BD.Admin` app admin?
 * @param {string[]} roles
 * @returns {boolean}
 */
export const isSuperuser = (roles) => intersects(asArray(roles), FULL_BYPASS);

/**
 * Can this role set perform `action`? The model is fail-closed: every user
 * now has an App Role, so a caller with no BD.* role and no superuser/BD.Admin
 * bypass is DENIED every gated action. Only `"*"` actions (rule b) stay open.
 *
 * Rules, evaluated in order:
 *   (a) full-access bypass (`de` / `admin` / `BD.Admin`) -> true
 *   (b) action's allowed list includes the `"*"` sentinel -> true
 *   (d) otherwise true iff the role set intersects the action's allowed list
 *
 * Unknown actions resolve via the same rules against an empty allowed list, so
 * they deny everyone but the full-access bypass — fail-safe for typos.
 *
 * @param {string[]} roles - the caller's roles (dev-override-aware when
 *   sourced via usePermissions)
 * @param {string} action - a key of PERMISSIONS
 * @returns {boolean}
 */
export const can = (roles, action) => {
  // Expanded, so a `BD.Land` holder matches the internal roles the action
  // lists without any action having to name the composite.
  const list = expandRoles(roles);
  const allowed = PERMISSIONS[action] || [];

  // (a) full-access bypass — platform superusers + BD.Admin app admin
  if (intersects(list, FULL_BYPASS)) return true;

  // (b) "any role" sentinel
  if (allowed.includes("*")) return true;

  // (d) explicit membership
  return intersects(list, allowed);
};

/**
 * May this role set file an attachment under `type` (an `attachment_type`
 * pick-list value)? Types not named in `ATTACHMENT_TYPE_ACTIONS` are open to
 * anyone who holds `attachments.upload`, which the caller checks separately.
 *
 * UI-only, like everything else here: it keeps a restricted category out of
 * the Type dropdown so the role can't pick it. The server stores whatever
 * `attachment_type` it is handed.
 *
 * @param {string[]} roles - the caller's roles
 * @param {string} type - an `attachment_type` pick-list value
 * @returns {boolean}
 */
export const canUploadAttachmentType = (roles, type) => {
  const action = ATTACHMENT_TYPE_ACTIONS[String(type ?? "").trim().toUpperCase()];
  if (!action) return true;
  return can(roles, action);
};

/**
 * May this role set WRITE a single deal field?
 *
 * Resolution order:
 *   1. `metaRow.editable_for_me` — the server's own answer, computed per
 *      request from the map the PUT enforces. Authoritative when present.
 *   2. the local `DEAL_FIELD_ACTIONS` mirror, via `can`.
 *
 * Callers still owe the ordinary `hide` / `editable` metadata checks; this
 * answers the role question only (except in case 1, where the server has
 * already folded `editable` in).
 *
 * Why it matters: the deal form is metadata-driven and serializes every
 * field it renders. Rendering a field the caller can't write produces a PUT
 * the server rejects wholesale — so what the form renders as an input and
 * what it puts in the body both have to respect this.
 *
 * @param {string[]} roles - the caller's roles
 * @param {string} field - deal column name
 * @param {object} [metaRow] - the field's `/metadata` row, when available
 * @returns {boolean}
 */
export const canEditField = (roles, field, metaRow) => {
  if (metaRow && typeof metaRow.editable_for_me === "boolean") {
    return metaRow.editable_for_me;
  }
  return can(roles, DEAL_FIELD_ACTIONS[field] || DEAL_FIELD_DEFAULT_ACTION);
};
